import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import snippetsService from '../../../../services/snippetsService';

const AddSnippetModal = ({
  show,
  onHide,
  onInsertSnippet,
  wabaId,
  accessToken
}) => {
  const [snippets, setSnippets] = useState([]);
  const [filteredSnippets, setFilteredSnippets] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSnippet, setSelectedSnippet] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Get user role from Redux store
  const user = useSelector(state => state.auth.user);
  const userRole = user?.role || accessToken?.user?.role || accessToken?.role;
  const role = userRole === 'manager' ? 'manager' : 'agent';

  // Load snippets when modal opens
  useEffect(() => {
    if (show && wabaId && accessToken) {
      loadSnippets();
    }
  }, [show, wabaId, accessToken]);

  // Filter snippets based on search term
  useEffect(() => {
    if (searchTerm.trim()) {
      const filtered = snippets.filter(snippet =>
        snippet.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        snippet.content.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSnippets(filtered);
    } else {
      setFilteredSnippets(snippets);
    }
  }, [snippets, searchTerm]);

  const loadSnippets = async () => {
    if (!accessToken) {
      setError('Missing required authentication information');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      let response;

      // Use appropriate API endpoint based on user role
      if (role === 'manager') {
        response = await snippetsService.getSnippets({
          accessToken,
          wabaId,
          limit: 100 // Load more snippets for selection
        });
      } else {
        // For agents and other roles, use agent endpoint
        response = await snippetsService.getAgentSnippets({
          accessToken,
          wabaId,
          limit: 100 // Load more snippets for selection
        });
      }

      if (response.success && response.data.snippets) {
        const transformedSnippets = response.data.snippets.map(snippet =>
          snippetsService.transformSnippetFromApi(snippet)
        );
        setSnippets(transformedSnippets);
      } else {
        throw new Error(response.message || 'Failed to load snippets');
      }
    } catch (error) {
      console.error('Error loading snippets:', error);
      setError(error.message || 'Failed to load snippets. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSnippetSelect = (snippet) => {
    setSelectedSnippet(snippet);
  };

  const handleInsertSnippet = () => {
    if (selectedSnippet && onInsertSnippet) {
      onInsertSnippet(selectedSnippet.content);
      onHide();
    }
  };

  const handleClose = () => {
    setSelectedSnippet(null);
    setSearchTerm('');
    setError(null);
    onHide();
  };

  if (!show) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1050
    }}>
      <div style={{
        backgroundColor: '#FFFFFF',
        borderRadius: '16px',
        width: '500px',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        boxShadow: '0px 3.75px 11px 0px rgba(0, 0, 0, 0.19), 0px 0.5px 5px 0px rgba(0, 0, 0, 0.04)'
      }}>
        {/* Cancel button */}
        <button
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '7.5px',
            right: '7.5px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            borderRadius: '4px',
            color: '#737C8A',
            fontSize: '20px',
            zIndex: 1
          }}
          onMouseEnter={(e) => e.target.style.backgroundColor = '#F8F9FA'}
          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
        >
          ×
        </button>

        {/* Header */}
        <div style={{
          padding: '24px 24px 8px 24px'
        }}>
          <h2 style={{
            fontFamily: 'Poppins',
            fontWeight: '600',
            fontSize: '16px',
            lineHeight: '1.5em',
            letterSpacing: '0.25%',
            color: '#344054',
            margin: 0
          }}>
            Add Snippet
          </h2>
        </div>

        {/* Search Input */}
        <div style={{
          padding: '0 24px',
          marginBottom: '24px'
        }}>
          <div style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '8px',
            padding: '10px 14px 10px 10px',
            border: '1px solid #D0D5DD',
            borderRadius: '4px',
            backgroundColor: '#FFFFFF'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'stretch',
              alignItems: 'stretch',
              gap: '8px',
              flex: 1
            }}>
              <input
                type="text"
                placeholder="Search snippet"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  flex: 1,
                  border: 'none',
                  outline: 'none',
                  fontFamily: 'Poppins',
                  fontWeight: '400',
                  fontSize: '13px',
                  lineHeight: '1.5em',
                  color: '#101828',
                  backgroundColor: 'transparent'
                }}
              />
            </div>
            <div style={{
              width: '16px',
              height: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M4 5.72L8 9.44L12 5.72" stroke="#616161" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
          </div>
        </div>

        {/* Snippets List */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignSelf: 'stretch',
          gap: '2px',
          padding: '0 24px',
          flex: 1,
          overflowY: 'visible',
          maxHeight: '300px',
          minHeight: '300px'
        }}>
          {isLoading && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#9CA8BB',
              fontFamily: 'Poppins',
              fontSize: '14px'
            }}>
              Loading snippets...
            </div>
          )}

          {error && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#DC3545',
              fontFamily: 'Poppins',
              fontSize: '14px'
            }}>
              {error}
            </div>
          )}

          {!isLoading && !error && filteredSnippets.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: '#9CA8BB',
              fontFamily: 'Poppins',
              fontSize: '14px'
            }}>
              {searchTerm ? 'No snippets found matching your search.' : 'No snippets available.'}
            </div>
          )}

          {!isLoading && !error && filteredSnippets.map((snippet) => (
            <div
              key={snippet.id}
              onClick={() => handleSnippetSelect(snippet)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignSelf: 'stretch',
                gap: '4px',
                padding: '8px 6px',
                border: selectedSnippet?.id === snippet.id ? '1px solid #0BA9E0' : '1px solid #E5E7F0',
                borderRadius: '4px',
                backgroundColor: selectedSnippet?.id === snippet.id ? '#EDE7F6' : '#FFFFFF',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                marginBottom: '2px'
              }}
              onMouseEnter={(e) => {
                if (selectedSnippet?.id !== snippet.id) {
                  e.currentTarget.style.backgroundColor = '#F8F9FA';
                }
              }}
              onMouseLeave={(e) => {
                if (selectedSnippet?.id !== snippet.id) {
                  e.currentTarget.style.backgroundColor = '#FFFFFF';
                }
              }}
            >
              <div style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                alignSelf: 'stretch',
                gap: '10px',
                padding: '4px 8px'
              }}>
                {/* Icon */}
                <div style={{
                  width: '17px',
                  height: '17px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.6708 6.8219L10.6604 3.8469L11.6521 2.85523C11.9236 2.5837 12.2571 2.44794 12.6526 2.44794C13.0481 2.44794 13.3816 2.5837 13.6531 2.85523L14.6448 3.8469C14.9163 4.11842 15.058 4.44603 15.0698 4.82971C15.0816 5.21339 14.9517 5.54099 14.6802 5.81252L13.6708 6.8219ZM12.6438 7.86669L5.13542 15.375H2.125V12.3646L9.63333 4.85627L12.6438 7.86669Z" fill={selectedSnippet?.id === snippet.id ? '#0BA9E0' : '#616161'} />
                  </svg>
                </div>

                {/* Content */}
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  flex: 1
                }}>
                  <div style={{
                    fontFamily: 'Poppins',
                    fontWeight: '400',
                    fontSize: '14px',
                    lineHeight: '1.5em',
                    color: '#494E50'
                  }}>
                    {snippet.name}
                  </div>
                  <div style={{
                    fontFamily: 'Poppins',
                    fontWeight: '400',
                    fontSize: '12px',
                    lineHeight: '1.5em',
                    color: '#9CA8BB',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {snippet.content}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'flex-end',
          alignItems: 'center',
          alignSelf: 'stretch',
          gap: '16px',
          padding: '24px'
        }}>
          <button
            onClick={handleInsertSnippet}
            disabled={!selectedSnippet}
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '8px',
              padding: '10px 18px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: selectedSnippet ? '#0BA9E0' : '#F2F4F7',
              color: selectedSnippet ? '#FFFFFF' : '#98A2B3',
              fontFamily: 'Poppins',
              fontWeight: '500',
              fontSize: '13px',
              lineHeight: '1.5em',
              cursor: selectedSnippet ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s ease',
              boxShadow: '0px 1px 2px rgba(16, 24, 40, 0.05)',
              width: '168px',
              height: '41px'
            }}
          >
            Insert Snippet
          </button>
        </div>
      </div>
    </div>
  );
};

AddSnippetModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onInsertSnippet: PropTypes.func.isRequired,
  wabaId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  accessToken: PropTypes.string
};

export default AddSnippetModal;
